import { cn } from '../cn';
import { formatPhoneDisplay } from '../ContactDetail/formatPhoneDisplay';
import Button from '../Input2/Button';

export interface CallerNumberLineProps {
  /**
   * The from-number to show. Null means nothing is pinned to this contact yet, so the copy names
   * the rotation's choice ("your next available number") instead of a number we can't know until
   * the dial resolves it server-side.
   */
  callerNumber: string | null;
  /** The number is no longer in the rep's bucket — renders a subtle warning under the line. */
  stale?: boolean;
  /** Present = render the guarded change affordance. Absent = the line is read-only. */
  onChangeNumber?: () => void;
  /**
   * Non-empty = the change affordance renders disabled, with this as its tooltip. Used by the
   * dialer, where the number can only be re-pinned once the live call is over.
   */
  changeDisabledReason?: string;
  className?: string;
}

/**
 * The shared "Calling from …" caption. Used by the in-call dialer (`Softphone`) and by the
 * pre-call contact-detail affordance (`ContactDialNumberPure`), so both surfaces word the
 * association the same way and pick up copy changes together.
 *
 * Purely presentational: the confirmation popup, the tRPC call and the "is there another number
 * to pick" decision all live with whoever renders this.
 */
export function CallerNumberLine({
  callerNumber,
  stale,
  onChangeNumber,
  changeDisabledReason,
  className,
}: CallerNumberLineProps) {
  return (
    <div className={cn('flex flex-col items-center gap-0.5', className)}>
      <div
        className="flex items-center justify-center gap-1.5 text-xs text-gray-500 dark:text-gray-400"
        data-test="caller-number">
        <span className="tabular-nums">
          {callerNumber == null
            ? 'Calling from your next available number'
            : `Calling from ${formatPhoneDisplay(callerNumber)}`}
        </span>
        {onChangeNumber && (
          <Button
            size="2xs"
            text
            disabled={changeDisabledReason != null}
            tooltip={changeDisabledReason}
            onClick={onChangeNumber}
            data-test="change-caller-number">
            {callerNumber == null ? 'Choose' : 'Change'}
          </Button>
        )}
      </div>
      {stale && (
        <p className="text-center text-xs text-amber-600 dark:text-amber-500" data-test="caller-number-stale">
          This number is no longer assigned to you
        </p>
      )}
    </div>
  );
}
